import React, { useRef, useEffect } from 'react';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import { IconUpload, IconFiles } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { tss } from 'tss-react/mui';
import { toast } from 'react-toastify';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import FilesModal from './AgentKnowledgeBaseUpload/FilesModal';
import CustomAlert from '@shared/components/alert/alert';
import useKnowledgeBaseFiles from '../hooks/useKnowledgeBaseFiles';

const useStyles = tss.create(() => ({
  kbSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontFamily: 'Plus Jakarta Sans',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '19.6px',
  },
  uploadButton: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '10px 14px',
    gap: '6px',
    height: '36px',
    background: '#F2F6FA',
    borderRadius: '8px',
    color: '#2A3547',
    fontWeight: 500,
    fontSize: '13px',
    textTransform: 'none' as const,
    width: 'fit-content',
    '&:hover': {
      background: '#E0E6ED',
    },
    '&.Mui-disabled': {
      background: '#EAEFF4',
      color: '#7C8FAC',
    },
  },
  hiddenInput: {
    display: 'none',
  },
}));

interface KnowledgeBaseFile {
  id: string;
  name: string;
  url?: string;
}

interface KnowledgeBaseUploaderProps {
  campaignId?: string;
  isDisabled: boolean;
}

export const KnowledgeBaseUploader: React.FC<KnowledgeBaseUploaderProps> = ({
  campaignId,
  isDisabled,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [modalOpen, setModalOpen] = React.useState(false);

  const {
    files: uploadedFiles,
    error,
    uploadFile,
    removeFile,
    isLoading,
    successMessage,
    clearSuccess,
  } = useKnowledgeBaseFiles(campaignId);

  // Show success toast when successMessage is set
  useEffect(() => {
    if (successMessage) {
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 5000,
        icon: false,
        data: {
          title: t('agentPage.knowledgeBase.success'),
          description: successMessage,
        },
      });
      clearSuccess();
    }
  }, [successMessage, clearSuccess, t]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const file = files[0];
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      await uploadFile(file);
    } catch (err) {
      console.error('File upload error:', err);
    }
  };

  const handleRemoveFile = async (id: string) => {
    try {
      await removeFile(id);
    } catch (err) {
      console.error('File removal error:', err);
    }
  };

  return (
    <Box className={classes.kbSection}>
      <Typography className={classes.label} variant="h6">
        {t('agentPage.knowledgeBase.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('agentPage.knowledgeBase.helperText')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleFileSelect}
          className={classes.hiddenInput}
          disabled={isDisabled}
        />
        <Button
          variant="contained"
          className={classes.uploadButton}
          onClick={() => fileInputRef.current?.click()}
          disabled={isDisabled || isLoading || !campaignId}
          startIcon={<IconUpload size={18} />}>
          {t('agentPage.knowledgeBase.uploadButton')}
        </Button>
        {uploadedFiles.length > 0 ? (
          <Tooltip title={t('agentPage.knowledgeBase.manageFiles')} arrow placement="top">
            <Button
              variant="contained"
              onClick={() => setModalOpen(true)}
              startIcon={<IconFiles size={18} />}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '10px 14px',
                gap: '6px',
                height: '36px',
                background: '#ECF2FF',
                borderRadius: '8px',
                color: '#1875FF',
                fontWeight: 500,
                fontSize: '13px',
                textTransform: 'none',
                border: '1px solid #1875FF',
                cursor: 'pointer',
                '&:hover': {
                  background: '#1875FF',
                  color: '#ffffff',
                  border: '1px solid #1875FF',
                },
              }}>
              {uploadedFiles.length}{' '}
              {uploadedFiles.length === 1
                ? t('agentPage.knowledgeBase.file')
                : t('agentPage.knowledgeBase.files')}
            </Button>
          </Tooltip>
        ) : (
          <Typography
            sx={{
              fontFamily: 'Plus Jakarta Sans',
              fontWeight: 400,
              fontSize: '13px',
              color: '#7C8FAC',
            }}>
            {t('agentPage.knowledgeBase.noFiles')}
          </Typography>
        )}
      </Box>
      <FilesModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        files={uploadedFiles}
        onRemoveFile={handleRemoveFile}
      />
      {error && <CustomAlert message={error} type="error" />}
    </Box>
  );
};
